import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {Injectable, OnInit} from '@angular/core';
import {User, UserManager} from 'oidc-client';
import {Observable, Subject, of, throwError, from, BehaviorSubject, interval, Subscription} from 'rxjs';
import {AuthContext} from '../Model/AuthContext';
import {UserProfile} from '../Model/UserProfile';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {environment} from '../../environments/environment';
import {AuthGuard} from './Auth-guard';
import { jwtDecode } from 'jwt-decode';

@Injectable({providedIn: 'root'})
export class AuthService implements OnInit{
    public userManager: UserManager;
    private user: User | null = null;
    private logoutInProgress = false;
    private loginCallbackPromise:Promise<User> | null = null;
    private loginChangedSubject = new Subject<boolean>();
    loginChanged = this.loginChangedSubject.asObservable();
    authContext: AuthContext | null = null;
    private profileLoadPromise: Promise<UserProfile> | null = null;
    private cache: any;
    private httpOptions: any;
    authGuard:AuthGuard
    // clientId: any = this.getClientId(); // this var is used lny for external clientId
    userType: any = null;
    userRoles: string[] = [];
    private tokenRefreshSubscription?: Subscription;
    private tokenRefreshPromise: Promise<User | null> | null = null;
    private tokenRefreshInProgress = false;

    constructor(private httpClient: HttpClient, private router: Router) {
        const idpSettings = {
            authority: environment.stsAuthority,
            client_id: environment.clientId,
            redirect_uri: `${environment.clientRoot}signin-callback`,
            scope: 'openid profile',
            response_type: 'code',
            post_logout_redirect_uri: `${environment.clientRoot}signout-callback`,
            automaticSilentRenew: false, // We control refresh ourselves every 10 minutes
            silent_redirect_uri: `${environment.clientRoot}assets/silent-callback.html`
        };

        // Initialize userManager here
        this.userManager = new UserManager(idpSettings);

        // this.userManager.events.addAccessTokenExpired(() => {
        //     this.loginChangedSubject.next(false);
        // });
        this.userManager.events.addAccessTokenExpired(
            async () => {
                console.warn(
                    'Access token expired. Attempting refresh.'
                );

                try {
                    await this.refreshToken();
                } catch (error) {
                    console.error(
                        'Expired-token recovery failed:',
                        error
                    );

                    await this.userManager.signinRedirect();
                }
            }
        );

        this.userManager.events.addUserLoaded(async (user: User) => {
            this.user = user;
            this.setUserTypeFromToken(user.access_token);

            try {
                await this.loadUserProfile();
                this.startTokenRefresh();
                this.loginChangedSubject.next(!!user && !user.expired);
            } catch (error) {
                console.error('Unable to load user profile:', error);
                this.loginChangedSubject.next(false);
            }
        });
    }

    ngOnInit(): void {
    }
    clientIdSubject = new BehaviorSubject<number | null>(
    sessionStorage.getItem('clientId') ? Number(JSON.parse(sessionStorage.getItem('clientId')!)) : null
    );

    clientId$ = this.clientIdSubject.asObservable();
    get clientId(): number | null {
    return this.clientIdSubject.value;
    }
    setClientId(id: any): void {
        // this.clientId = id;
        // console.log('clientId', this.clientId);
        const num = Number(id);
        sessionStorage.setItem('clientId', JSON.stringify(num));
        this.clientIdSubject.next(num);
        console.log('clientId', num);
    }

    clearClientId(): void {
    sessionStorage.removeItem('clientId');
    this.clientIdSubject.next(null);
    }

    getClientId(): number | null {
        const saved = sessionStorage.getItem('clientId');
        return saved ? Number(JSON.parse(saved)) : null;
    }

    startTokenRefresh(): void {
        if (this.tokenRefreshSubscription) {
            return;
        }

        this.tokenRefreshSubscription =
            interval(10 * 60 * 1000)
                .subscribe(() => {
                    this.refreshToken().catch(error => {
                        console.error(
                            'Scheduled token refresh failed:',
                            error
                        );
                    });
                });
    }

    // private async refreshToken(): Promise<void> {
    //     if (this.tokenRefreshInProgress) {
    //         return;
    //     }

    //     try {
    //         const currentUser = await this.userManager.getUser();

    //         if (!currentUser) {
    //             return;
    //         }

    //         this.tokenRefreshInProgress = true;

    //         const refreshedUser = await this.userManager.signinSilent();

    //         if (refreshedUser) {
    //             this.user = refreshedUser;
    //             this.setUserTypeFromToken(refreshedUser.access_token);
    //         }
    //     } catch (error) {
    //         console.error('Token refresh failed:', error);
    //     } finally {
    //         this.tokenRefreshInProgress = false;
    //     }
    // }
    async refreshToken(): Promise<User | null> {

        if (this.tokenRefreshPromise) {
            return this.tokenRefreshPromise;
        }

        this.tokenRefreshPromise =
            this.performTokenRefresh();

        try {
            return await this.tokenRefreshPromise;
        } finally {
            this.tokenRefreshPromise = null;
        }
    }

private async performTokenRefresh():
    Promise<User | null> {

    const currentUser =
        await this.userManager.getUser();

    if (!currentUser) {
        return null;
    }

    try {

        const refreshedUser =
            await this.userManager.signinSilent();

        if (refreshedUser) {

            this.user = refreshedUser;

            this.setUserTypeFromToken(
                refreshedUser.access_token
            );

            console.log(
                'Access token successfully refreshed.'
            );

            return refreshedUser;
        }

        return null;

    } catch (error) {

        console.error(
            'Silent token refresh failed:',
            error
        );

        throw error;
    }
}    

    stopTokenRefresh(): void {
        if (this.tokenRefreshSubscription) {
            this.tokenRefreshSubscription.unsubscribe();
            this.tokenRefreshSubscription = undefined;
        }
    }

    getTimeZone(): string {
      return JSON.parse(sessionStorage.getItem('timeZone') || '"UTC"');
    }

    setUserType(type: string | null ): void {
        this.userType = type ? type.toUpperCase() : null;

        if (this.userType) {
            sessionStorage.setItem('userType', this.userType);
        } else {
            sessionStorage.removeItem('userType');
        }
    }

    getUserType(): string | null {
    if (this.userType) {
        return this.userType;
    }

    const storedUserType = sessionStorage.getItem('userType');

    if (storedUserType) {
        this.userType = storedUserType.toUpperCase();
    }

    return this.userType;
    }

    isExternalUser(): boolean {
    return this.getUserType() === 'EXTERNAL';
    }

    isInternalUser(): boolean {
    return this.getUserType() === 'INTERNAL';
    }

    setRoles(roles: string[]): void {
        this.userRoles = (roles || []).map((role: string) => {
            return role.indexOf('ROLE_') === 0 ? role.substring(5) : role;
        });
        sessionStorage.setItem('userRoles', JSON.stringify(this.userRoles));
        console.log('roles', this.userRoles);
    }

    getRoles(): string[] {
        if (!this.userRoles.length) {
            const savedRoles = sessionStorage.getItem('userRoles');
            if (savedRoles) {
                try {
                    this.userRoles = JSON.parse(savedRoles);
                } catch (error) {
                    this.userRoles = [];
                }
            }
        }
        return this.userRoles;
    }

    hasRole(role: string): boolean {
        const normalizedRole = role.indexOf('ROLE_') === 0 ? role.substring(5) : role;
        return this.getRoles().includes(normalizedRole);
    }

    hasAnyRole(roles: string[]): boolean {
        return roles.some(role => this.hasRole(role));
    }

    login(): Promise<void> {
        return this.userManager.signinRedirect();
    }

    // completeLogin(): Promise<User> {
    // return this.userManager.signinRedirectCallback()
    // .then((user: User) => {
    //   this.user = user;
    //   this.setUserTypeFromToken(user.access_token);

    //   return this.loadUserProfile().then(() => {
    //     this.loginChangedSubject.next(
    //       !!user && !user.expired
    //     );

    //     return user;
    //   });
    // });
    // }
    completeLogin(): Promise<User> {
        if (this.loginCallbackPromise) {
            return this.loginCallbackPromise;
        }

        this.loginCallbackPromise = this.userManager
            .signinRedirectCallback()
            .then(async (user: User) => {
                this.user = user;
                this.setUserTypeFromToken(user.access_token);

                // Do not leave the callback until profile, roles and timezone are ready.
                await this.loadUserProfile();

                this.startTokenRefresh();
                this.loginChangedSubject.next(!!user && !user.expired);

                return user;
            })
            .finally(() => {
                this.loginCallbackPromise = null;
            });

        return this.loginCallbackPromise;
    }

    private setUserTypeFromToken(token: string): void {
    if (!token) {
        this.setUserType(null);
        return;
    }

    try {
        const decodedToken: any = jwtDecode(token);

        this.setUserType(decodedToken.userType || null);
    } catch (error) {
        console.error('Unable to decode user type from token', error);
        this.setUserType(null);
    }
    }

    // Updated initAuthSession method
    async initAuthSession(): Promise<User> {
        try {
            const user = await this.userManager.getUser();

            if (!user || user.expired) {
                await this.userManager.signinRedirect();
                throw new Error('User needs to sign in');
            }

            this.user = user;
            this.setUserTypeFromToken(user.access_token);

            if (!this.authContext) {
                await this.loadUserProfile();
            }

            this.startTokenRefresh();
            return user;
        } catch (error) {
            console.error('Error initializing auth session:', error);
            throw error;
        }
    }

    getAuthContext(): any {
        return this.authContext;
    }

    // Updated isLoggedIn method
    // isLoggedIn(): Promise<boolean> {
    //     return this.userManager.getUser().then(user => {
    //         this.user = user;

    //         const userCurrent = !!user && !user.expired;

    //         // Emit login status change if the user session has changed
    //         if (this.user !== user) {
    //             this.loginChangedSubject.next(userCurrent);
    //         }

    //         // Wait for roles before route guards/navbar/home evaluate provider access.
    //         if (userCurrent && !this.authContext) {
    //             return this.loadUserProfile()
    //                 .then(() => true)
    //                 .catch(() => false);
    //         }

    //         return userCurrent;
    //     }).catch(error => {
    //         console.error('Error checking user login status:', error);
    //         return false;  // If there's an error (e.g., network issue), return false
    //     });
    // }
    async isLoggedIn(): Promise<boolean> {
        try {
            const user = await this.userManager.getUser();

            if (!user || user.expired) {
                this.user = null;
                return false;
            }

            this.user = user;
            this.setUserTypeFromToken(user.access_token);

            if (!this.authContext) {
                await this.loadUserProfile();
            }

            this.startTokenRefresh();
            return true;
        } catch (error) {
            console.error('Error checking login state:', error);
            return false;
        }
    }

    // logout() {
    //     // Ensure the user is logged in and get the id_token_hint
    //     this.userManager.getUser().then(user => {
    //         if (user) {
    //             // Include the id_token_hint in the signout redirect
    //             this.userManager.signoutRedirect().then(() => {

    //                 localStorage.clear();
    //                 sessionStorage.clear()// Clear local storage if needed
    //             }).catch(error => {
    //                 console.error('Error during logout:', error);
    //             });
    //         }
    //     }).catch(error => {
    //         console.error('Error fetching user session for logout:', error);
    //     });
    // }
    

    async logout(): Promise<void> {
        if (this.logoutInProgress) {
            return;
        }

        this.logoutInProgress = true;
        this.stopTokenRefresh();

        try {
            const user = await this.userManager.getUser();

            if (!user || !user.id_token) {
                await this.userManager.removeUser();
                this.clearLocalAuthenticationState();
                this.logoutInProgress = false;
                return;
            }

            await this.userManager.signoutRedirect({
                id_token_hint: user.id_token,
                post_logout_redirect_uri: `${environment.clientRoot}signout-callback`
            });
        } catch (error) {
            this.logoutInProgress = false;
            console.error('Error during logout:', error);
        }
    }

    async completeLogout(): Promise<void> {
        try {
            await this.userManager.signoutRedirectCallback();
        } catch (error) {
            console.error('Error completing logout callback:', error);
        } finally {
            this.logoutInProgress = false;
            this.stopTokenRefresh();
            this.clearLocalAuthenticationState();
        }
    }

    private clearLocalAuthenticationState(): void {
        this.user = null;
        this.authContext = null;
        this.profileLoadPromise = null;
        this.userType = null;
        this.userRoles = [];

        this.clearClientId();

        sessionStorage.removeItem('userType');
        sessionStorage.removeItem('userRoles');
        sessionStorage.removeItem('timeZone');
        sessionStorage.removeItem('externalWelcomeSplashShown');
        sessionStorage.removeItem('lastUsedFilterParams');
        sessionStorage.removeItem('patientFilters');
        sessionStorage.removeItem('patientDuplicateFilters');

        this.loginChangedSubject.next(false);
    }

    getAccessToken(): Promise<string | null> {
        return this.userManager.getUser().then((user) => {
            return user && !user.expired ? user.access_token : null;
        });
    }

    // loadUserProfile(): Promise<UserProfile> {
    //     return this.httpClient.get<UserProfile>(environment.apiRoot + environment.userService + 'userprofile')
    //       .pipe(tap(next => {
    //             this.authContext = new AuthContext();
    //             this.authContext.userProfile = next;

    //           // Save timezone to sessionStorage
    //           if (next.timeZone) {
    //             sessionStorage.setItem('timeZone', JSON.stringify(next.timeZone));
    //           }
    //           this.setRoles(next.authorities || []);
    //         }))
    //       .toPromise();
    // }
    loadUserProfile(): Promise<UserProfile> {
    if (this.authContext) {
        return Promise.resolve(
        this.authContext.userProfile
        );
    }

    if (this.profileLoadPromise) {
        return this.profileLoadPromise;
    }

    this.profileLoadPromise =
        this.httpClient
        .get<UserProfile>(
            environment.apiRoot +
            environment.userService +
            'userprofile'
        )
        .pipe(
            tap((profile: UserProfile) => {
            this.authContext =
                new AuthContext();

            this.authContext.userProfile =
                profile;

            if (profile.timeZone) {
                sessionStorage.setItem(
                'timeZone',
                JSON.stringify(
                    profile.timeZone
                )
                );
            }

            this.setRoles(
                profile.authorities || []
            );
            })
        )
        .toPromise()
        .then((profile: UserProfile) => {
            this.profileLoadPromise = null;
            return profile;
        })
        .catch(error => {
            this.profileLoadPromise = null;
            throw error;
        });

    return this.profileLoadPromise;
    }    
    private formatErrors(error: any) {
        return throwError(error.error);
    }

    private getHeader(): Observable<any> {
        return from(this.getAccessToken()).pipe(
            map((tokenData: any) => {
                const token = tokenData;
                return {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${token}` // Correctly format Bearer token
                    })
                };
            })
        );
    }

    getHeaderExport(): Observable<any> {
        return from(this.getAccessToken()).pipe(
            map((tokenData: any) => {
                const token = tokenData;
                return of({
                headers: new HttpHeaders({
                Authorization: `Bearer ${token}`,
                Accept: 'application/json'
                })
    });
            })
        );

    }

    // private handleAuthError(error: any): Observable<any> {
    //     if (error.status === 401) {
    //         this.completeLogout();
    //         this.router.navigate(['/']);
    //     }
    //     throw error;
    // }

    // Get HTTP request
    private handleAuthError(error: HttpErrorResponse): Observable<any> {
        if (error.status === 401) {
            this.handleUnauthorized();
        }

        // Do not globally redirect on every 403. Some dashboard/widget APIs may
        // legitimately be unavailable to a role. Route authorization belongs
        // in AuthGuard; the component can handle an API-specific 403 itself.
        return throwError(error);
    }

    private async handleUnauthorized(): Promise<void> {
        try {
            const user = await this.userManager.getUser();

            if (user && user.id_token) {
                await this.logout();
                return;
            }

            await this.userManager.removeUser();
            this.stopTokenRefresh();
            this.clearLocalAuthenticationState();
        } catch (error) {
            console.error('Error handling unauthorized response:', error);
        }
    }

    get(path: string): Observable<any> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                return this.httpClient.get(`${environment.apiRoot}${path}`, httpOptions);
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }

    getP(path: string, params?: any): Observable<any> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                if (params) {
                    let httpParams = new HttpParams();
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                    httpOptions.params = httpParams;
                }

                return this.httpClient.get(`${environment.apiRoot}${path}`, httpOptions);
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }

    getWithHttpParams(path: string, params: HttpParams): Observable<any> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                httpOptions.params = params;
                return this.httpClient.get(`${environment.apiRoot}${path}`, httpOptions);
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }

    getAbsoluteP(path: string, params?: any): Observable<any> {
      return this.getHeader().pipe(
        switchMap(httpOptions => {
          if (params) {
            let httpParams = new HttpParams();

            Object.keys(params).forEach(key => {
              const value = params[key];

              if (value !== null && value !== undefined) {
                if (Array.isArray(value)) {
                  value.forEach(item => {
                    httpParams = httpParams.append(key, item.toString());
                  });
                } else {
                  httpParams = httpParams.set(key, value.toString());
                }
              }
            });

            httpOptions.params = httpParams;
          }

          return this.httpClient.get(`${environment.apiRoot}${path}`, httpOptions);
        }),
        catchError((x) => this.handleAuthError(x))
      );
    }
    getAbsolute(url: string): Observable<any> {
      return this.getHeader().pipe(
        switchMap(httpOptions => this.httpClient.get(url, httpOptions)),
        catchError(error => this.handleAuthError(error))
      );
    }

    postAbsolute(url: string, body: any = null, params?: any): Observable<any> {
      return this.getHeader().pipe(
        switchMap(httpOptions => {
          if (params) {
            let httpParams = new HttpParams();

            Object.keys(params).forEach(key => {
              const value = params[key];

              if (value !== null && value !== undefined) {
                httpParams = httpParams.set(key, value.toString());
              }
            });

            httpOptions.params = httpParams;
          }

          return this.httpClient.post(url, body, httpOptions);
        }),
        catchError((x) => this.handleAuthError(x))
      );
    }

    postAbsoluteFormData(url: string, body: FormData, params?: any): Observable<any> {
    return this.getHeader().pipe(
        switchMap(httpOptions => {
        if (params) {
            let httpParams = new HttpParams();
            Object.keys(params).forEach(key => {
            httpParams = httpParams.set(key, params[key]);
            });
            httpOptions.params = httpParams;
        }

        if (httpOptions.headers?.has('Content-Type')) {
            httpOptions.headers = httpOptions.headers.delete('Content-Type');
        }

        return this.httpClient.post(url, body, httpOptions);
        }),
        catchError((x) => this.handleAuthError(x))
    );
    }

    getPost(path: string, body?: any, params?: any): Observable<any> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                if (params) {
                    let httpParams = new HttpParams();
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                    httpOptions.params = httpParams;
                }
                return this.httpClient.post(`${environment.apiRoot}${path}`, body, httpOptions);
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }

    getPDFView(path: string, params?: any, responseType: 'json' | 'blob' = 'json'): Observable<any> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                let httpParams = new HttpParams();
                if (params) {
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                }

                return this.httpClient.get(`${environment.apiRoot}${path}`, {
                    ...httpOptions, // Spread httpOptions (headers, etc.)
                    params: httpParams, // Set params correctly
                    responseType: responseType as 'json' | 'blob' // Pass responseType correctly
                });
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }

    getView(path: string, params?: any, responseType: 'json' | 'blob' = 'json'): Observable<HttpResponse<Blob>> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                let httpParams = new HttpParams();
                if (params) {
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                }

                return this.httpClient.get(`${environment.apiRoot}${path}`, {
                    ...httpOptions,
                    params: httpParams,
                    responseType: responseType as 'json' | 'blob',
                    observe: 'response' // 👈 Important: return full response with headers
                });
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }



    /*getPDF(path: string, params?: any, responseType: 'json' | 'blob' = 'json'): Observable<any> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                if (params) {
                    let httpParams = new HttpParams();
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                    httpOptions.params = httpParams;
                }

                // Set responseType for file downloads
                httpOptions.responseType = responseType as 'json' | 'blob';

                return this.httpClient.get(`${environment.apiRoot}${path}`, httpOptions);
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }*/

    // Post HTTP request
    post(path: string, body: any): Observable<any> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                return this.httpClient.post(`${environment.apiRoot}${path}`, JSON.stringify(body), httpOptions);
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }

    postD(path: string, body: any, params?: any): Observable<any> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                if (params) {
                    let httpParams = new HttpParams();
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                    httpOptions.params = httpParams;
                }
                return this.httpClient.post(`${environment.apiRoot}${path}`, body, httpOptions);
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }

    postP(path: string, body?: any, params?: any): Observable<any> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                if (params) {
                    let httpParams = new HttpParams();
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                    httpOptions.params = httpParams;
                }
                return this.httpClient.post(`${environment.apiRoot}${path}`, body, httpOptions);
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }

    // Put HTTP request
    put(path: string, body?: any, params?: any): Observable<any> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                if (params) {
                    let httpParams = new HttpParams();
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                    httpOptions.params = httpParams;
                }

                return this.httpClient.put(`${environment.apiRoot}${path}`, JSON.stringify(body), httpOptions);
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }

    // Delete HTTP request
    delete(path: string, params?: any): Observable<HttpResponse<any>> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                if (params) {
                    let httpParams = new HttpParams();
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                    httpOptions.params = httpParams;
                }

                return this.httpClient.delete<any>(`${environment.apiRoot}${path}`, {
                    ...httpOptions,
                    observe: 'response'
                }) as Observable<HttpResponse<any>>;
            }),
            catchError((error) => this.handleAuthError(error))
        );
    }

    patchP(path: string, body?: any, params?: any): Observable<any> {
        return this.getHeader().pipe(
            switchMap(httpOptions => {
                if (params) {
                    let httpParams = new HttpParams();
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                    httpOptions.params = httpParams;
                }
                return this.httpClient.patch(`${environment.apiRoot}${path}`, body, httpOptions);
            }),
            catchError((x) => this.handleAuthError(x))
        );
    }

  patchB(path: string, params?: Record<string, any>): Observable<any> {
    return this.getHeader().pipe(
      switchMap(httpOptions => {
        // Build HttpParams from your key/value object
        let httpParams = new HttpParams();
        if (params) {
          Object.keys(params).forEach(key => {
            const val = params[key];
            if (val !== undefined && val !== null) {
              // ensure everything is a string in query
              httpParams = httpParams.set(key, String(val));
            }
          });
        }

        // Merge params into options (preserve existing headers, etc.)
        const options = { ...httpOptions, params: httpParams };

        // 👇 send PATCH with **no body**, only headers+params
        return this.httpClient.patch(`${environment.apiRoot}${path}`, null, options);
      }),
      catchError((x) => this.handleAuthError(x))
    );
  }

    // getPDF(path: string, body?: any, params?: any): Observable<any> {
    //     return this.getHeaderPDF().pipe(
    //         switchMap(httpOptions => {
    //             // Add request parameters if any
    //             if (params) {
    //                 let httpParams = new HttpParams();
    //                 Object.keys(params).forEach(key => {
    //                     httpParams = httpParams.set(key, params[key]);
    //                 });
    //                 httpOptions.params = httpParams;
    //                 httpOptions.params = httpParams;
    //             }
    //
    //             // Set the response type to 'blob'
    //             httpOptions.responseType = 'blob' as 'json';
    //
    //             // Use POST if body is provided, otherwise GET
    //             if (body) {
    //                 return this.httpClient.post(`${environment.report}${path}`, body, httpOptions);
    //             } else {
    //                 debugger
    //                 return this.httpClient.get(`${environment.report}${path}`, httpOptions);
    //             }
    //         }),
    //         catchError((error) => this.handleAuthError(error))
    //     );
    // }
    /*getPDF(path: string, body?: any, params?: any): Observable<any> {
        return this.getHeaderPDF().pipe(
            switchMap(httpOptions => {
                // Add query parameters if provided
                let httpParams = new HttpParams();
                if (params) {
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                }

                // Create a new httpOptions object to avoid modifying the original
                const options = {
                    ...httpOptions,
                    params: httpParams,
                    responseType: 'blob' as 'json',
                };

                // Use POST if body is provided, otherwise GET
                if (body) {
                    return this.httpClient.post(`${environment.apiRoot}${path}`, body, options);
                } else {
                    return this.httpClient.get(`${environment.apiRoot}${path}`, options);
                }
            }),
            catchError((error) => this.handleAuthError(error))
        );
    }*/
    getPDF(path: string, body?: any, params?: any): Observable<HttpResponse<Blob>> {
        return this.getHeaderPDF().pipe(
            switchMap(httpOptions => {
                let httpParams = new HttpParams();
                if (params) {
                    Object.keys(params).forEach(key => {
                        httpParams = httpParams.set(key, params[key]);
                    });
                }

                const options = {
                    ...httpOptions,
                    params: httpParams,
                    responseType: 'blob' as 'json',
                    observe: 'response' as 'body'
                };

                if (body) {
                    return this.httpClient.post<HttpResponse<Blob>>(`${environment.apiRoot}${path}`, body, options);
                } else {
                    return this.httpClient.get<HttpResponse<Blob>>(`${environment.apiRoot}${path}`, options);
                }
            }),
            catchError((error) => this.handleAuthError(error))
        );
    }




    private getHeaderPDF(): Observable<any> {
        return from(this.getAccessToken()).pipe(
            map((tokenData: any) => {
                const token = tokenData;
                return {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        // 'Authorization': token ? "Bearer " + token : '' // Correctly format Bearer token
                        'Authorization': `Bearer ${token}`
                    })
                };
            })
        );
    }

    // getPDF(path: string, body?: any, params?: any, format: 'excel' | 'pdf' = 'pdf'): Observable<any> {
    //     return this.getHeaderPDF(format).pipe(
    //         switchMap(httpOptions => {
    //             // Add query parameters if provided
    //             let httpParams = new HttpParams();
    //             if (params) {
    //                 Object.keys(params).forEach(key => {
    //                     httpParams = httpParams.set(key, params[key]);
    //                 });
    //             }
    //
    //             // Create a new httpOptions object to avoid modifying the original
    //             const options = {
    //                 ...httpOptions,
    //                 params: httpParams,
    //                 responseType: 'blob' as 'json', // Ensure response is treated as a blob
    //             };
    //
    //             // Use POST if body is provided, otherwise GET
    //             if (body) {
    //                 return this.httpClient.post(`${environment.apiRoot}${path}`, body, options);
    //             } else {
    //                 return this.httpClient.get(`${environment.apiRoot}${path}`, options);
    //             }
    //         }),
    //         catchError((error) => this.handleAuthError(error))
    //     );
    // }
    //
    // private getHeaderPDF(format: 'excel' | 'pdf'): Observable<any> {
    //     return from(this.getAccessToken()).pipe(
    //         map((tokenData: any) => {
    //             const token = tokenData;
    //             // Dynamically set the Accept header based on the requested format
    //             const acceptHeader = format === 'pdf' ? 'application/pdf' : 'application/vnd.ms-excel';
    //
    //             return {
    //                 headers: new HttpHeaders({
    //                     'Content-Type': 'application/json',
    //                     'Accept': acceptHeader,
    //                     'Authorization': `Bearer ${token}` // Correctly format Bearer token
    //                 })
    //             };
    //         })
    //     );
    // }

}
